<template>
  <div class="appointments-scanner-widget">
    <div class="appointments-scanner-widget__controls">
      <div class="appointments-scanner-widget__field">
        <label :for="cameraSelectId" class="appointments-scanner-widget__label">{{ labels.selectCamera }}</label>
        <select
          :id="cameraSelectId"
          v-model="selectedCameraId"
          class="theme-select__item appointments-scanner-widget__select"
          :disabled="!cameras.length"
          @change="onCameraChange"
        >
          <option v-for="camera in cameras" :key="camera.id" :value="camera.id">{{ camera.label }}</option>
        </select>
      </div>

      <div class="appointments-scanner-widget__actions">
        <button
          ref="startButton"
          type="button"
          class="theme-btn theme-btn--primary"
          :disabled="!canStart"
          @click="handleStart"
        >{{ labels.startScanning }}</button>
        <button
          type="button"
          class="theme-btn theme-btn--white-outlined"
          :disabled="!isRunning"
          @click="handleStop"
        >{{ labels.stopScanning }}</button>
      </div>
    </div>

    <div class="appointments-scanner-widget__viewport">
      <div :id="readerId" class="appointments-scanner-widget__reader"></div>
    </div>

    <p class="appointments-scanner-widget__status" role="status" aria-live="polite">{{ statusMessage }}</p>

    <div
      v-if="resultVisible"
      class="modal modal--bottom appointments-scanner-widget__modal"
      style="display: flex;"
      @keydown.esc="closeResult"
    >
      <div class="modal__content" ref="resultContent" tabindex="-1">
        <button type="button" class="modal__close" :aria-label="labels.close" @click="closeResult">
          <i class="icon-close"></i>
        </button>

        <h3 class="modal__title" ref="resultHeading" tabindex="-1">{{ labels.candidateMarkedPresent }}</h3>

        <dl class="appointments-scanner-widget__result-list">
          <div class="appointments-scanner-widget__result-row">
            <dt>{{ labels.candidate }}</dt>
            <dd>{{ result.candidateNames || '—' }}</dd>
          </div>
          <div class="appointments-scanner-widget__result-row">
            <dt>{{ labels.scheduledAt }}</dt>
            <dd>{{ result.scheduledAt || '—' }}</dd>
          </div>
          <div class="appointments-scanner-widget__result-row">
            <dt>{{ labels.property }}</dt>
            <dd>{{ result.propertyName || '—' }}</dd>
          </div>
          <div class="appointments-scanner-widget__result-row">
            <dt>{{ labels.present }}</dt>
            <dd>
              <i
                :class="result.wasPresent
                  ? 'icon-check appointments-scanner-widget__icon--success'
                  : 'icon-close appointments-scanner-widget__icon--muted'"
              ></i>
            </dd>
          </div>
          <div class="appointments-scanner-widget__result-row">
            <dt>{{ labels.promising }}</dt>
            <dd>
              <button
                type="button"
                class="appointments-scanner-widget__promising-btn"
                :disabled="promisingLoading"
                :aria-label="result.isPromising ? labels.unmarkAsPromising : labels.markAsPromising"
                :aria-pressed="result.isPromising ? 'true' : 'false'"
                @click="togglePromising"
              >
                <span
                  v-if="result.isPromising"
                  class="fa fa-bookmark manage-appointments__icon manage-appointments__icon--approved"
                ></span>
                <span v-else class="fa fa-bookmark-o manage-appointments__icon manage-appointments__icon--disabled"></span>
              </button>
            </dd>
          </div>
        </dl>

        <div class="appointments-scanner-widget__modal-actions">
          <button type="button" class="theme-btn theme-btn--white-outlined" @click="closeResult">{{ labels.close }}</button>
          <button type="button" class="theme-btn theme-btn--primary" @click="scanNext">{{ labels.scanNextCandidate }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Html5Qrcode } from 'html5-qrcode';

function csrfToken() {
  var meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : '';
}

function isPermissionError(error) {
  var name = error && error.name ? error.name : '';
  var message = typeof error === 'string' ? error : (error && error.message) || '';
  return name === 'NotAllowedError' || /denied/i.test(message) || /permission/i.test(message);
}

// html5-qrcode rejects with a plain string (not an Error) when the page itself isn't a secure
// context (https or localhost) — the browser never even offers a permission prompt in that case.
function isInsecureContextError(error) {
  var message = typeof error === 'string' ? error : (error && error.message) || '';
  return /secure context/i.test(message);
}

function pickPreferredCameraId(cameras) {
  var back = cameras.find(function (camera) {
    return /back|rear|environment/i.test(camera.label || '');
  });
  return back ? back.id : cameras[0].id;
}

export default {
  name: 'AssignmentScanner',
  props: {
    scanUrl: {
      type: String,
      required: true,
    },
    promisingUrlTemplate: {
      type: String,
      required: true,
    },
    labels: {
      type: Object,
      required: true,
    },
  },
  data: function () {
    return {
      cameras: [],
      selectedCameraId: '',
      state: 'idle',
      statusMessage: '',
      isProcessing: false,
      manuallyStopped: false,
      cameraActive: false,
      resultVisible: false,
      result: null,
      promisingLoading: false,
    };
  },
  computed: {
    isRunning: function () {
      return this.state === 'active';
    },
    canStart: function () {
      return !!this.selectedCameraId && this.state !== 'active' && this.state !== 'starting';
    },
    cameraSelectId: function () {
      return 'assignment-scanner-camera-' + (this._uid || 0);
    },
    readerId: function () {
      return 'assignment-scanner-reader-' + (this._uid || 0);
    },
  },
  mounted: function () {
    this.qr = new Html5Qrcode(this.readerId);
    // Plain (non-reactive) promise chain, same pattern as `this.qr` — every lifecycle-mutating
    // action (start / stop+clear / camera switch) is appended to it via runExclusive() so the
    // underlying Html5Qrcode instance never has two of start()/stop()/clear() in flight at once.
    this.transitionQueue = Promise.resolve();
    this.loadCameras();
  },
  beforeDestroy: function () {
    this.manuallyStopped = true;
    this.fullStop();
  },
  methods: {
    // Serializes lifecycle work so rapid Start/Stop/camera-change interactions can never run
    // qr.start()/stop()/clear() concurrently. `work` always eventually resolves (its own
    // internals swallow expected failures) so the queue itself never gets stuck on a rejection.
    runExclusive: function (work) {
      var settled = this.transitionQueue.then(work);
      this.transitionQueue = settled.catch(function () {});
      return settled;
    },
    loadCameras: function () {
      var self = this;
      return Html5Qrcode.getCameras().then(function (cameras) {
        self.cameras = cameras || [];

        if (!self.cameras.length) {
          self.state = 'no-camera';
          self.statusMessage = self.labels.cameraNotFound;
          return;
        }

        self.selectedCameraId = pickPreferredCameraId(self.cameras);
      }).catch(function (error) {
        self.cameras = [];
        if (isInsecureContextError(error)) {
          self.state = 'insecure-context';
          self.statusMessage = self.labels.insecureContext;
        } else if (isPermissionError(error)) {
          self.state = 'permission-denied';
          self.statusMessage = self.labels.cameraPermissionDenied;
        } else {
          self.state = 'no-camera';
          self.statusMessage = self.labels.cameraNotFound;
        }
      });
    },
    // Actually starts `this.selectedCameraId`. Always resolves — success and every known
    // failure both land in a definite, non-stale state/statusMessage. Only ever called from
    // inside runExclusive() (handleStart / onCameraChange) so it never overlaps a stop/clear.
    startCamera: function () {
      var self = this;
      if (!this.selectedCameraId) {
        return Promise.resolve();
      }

      this.manuallyStopped = false;
      this.state = 'starting';
      this.statusMessage = this.labels.startingCamera;

      return this.qr.start(
        this.selectedCameraId,
        { fps: 10, qrbox: { width: 250, height: 250 } },
        function (decodedText) {
          self.onDecoded(decodedText);
        },
        function () {
          // Ignore per-frame decode failures (expected while no code is in view).
        }
      ).then(function () {
        self.cameraActive = true;
        self.state = 'active';
        self.statusMessage = self.labels.cameraFound;
      }).catch(function (error) {
        self.cameraActive = false;
        if (isInsecureContextError(error)) {
          self.state = 'insecure-context';
          self.statusMessage = self.labels.insecureContext;
        } else if (isPermissionError(error)) {
          self.state = 'permission-denied';
          self.statusMessage = self.labels.cameraPermissionDenied;
        } else {
          self.state = 'no-camera';
          self.statusMessage = self.labels.cameraNotFound;
        }
      });
    },
    // Stops the current camera (if any) and, when `clearPreview` is true, wipes the reader div.
    // Always resolves, regardless of whether the camera was active, qr.stop() rejects, or
    // qr.clear() throws — cleanup failures must never block a caller from reaching a
    // consistent stopped state. html5-qrcode v2.3.8's clear() is synchronous (returns void,
    // not a Promise), so it is called directly rather than chained with .then()/.catch().
    stopCamera: function (clearPreview) {
      var self = this;
      var wasActive = this.cameraActive;
      var stopStep = wasActive ? this.qr.stop().catch(function () {}) : Promise.resolve();
      return stopStep.then(function () {
        self.cameraActive = false;
        if (clearPreview) {
          try {
            self.qr.clear();
          } catch (e) {
            // ignore — best-effort DOM cleanup, the camera is already considered stopped
          }
        }
      });
    },
    handleStart: function () {
      if (this.isRunning || this.state === 'starting' || !this.selectedCameraId) {
        return;
      }
      return this.runExclusive(this.startCamera.bind(this));
    },
    handleStop: function () {
      this.manuallyStopped = true;
      var self = this;
      return this.fullStop().then(function () {
        self.statusMessage = self.labels.scannerStopped;
      });
    },
    onCameraChange: function () {
      var self = this;
      if (!this.cameraActive) {
        return;
      }
      return this.runExclusive(function () {
        // Set this up front, before stopping the old camera, so the UI never shows the old
        // "Camera found. Scanning." status next to a preview that has already gone blank.
        self.state = 'starting';
        self.statusMessage = self.labels.startingCamera;
        return self.stopCamera(true).then(function () {
          return self.startCamera();
        });
      }).catch(function () {
        // startCamera()/stopCamera() already resolve on every known failure mode; reaching
        // here means something unexpected broke the chain. Never leave the switch stuck
        // showing a stale active state with a blank preview.
        self.cameraActive = false;
        self.state = 'no-camera';
        self.statusMessage = self.labels.cameraNotFound;
      });
    },
    // Stop without clearing the preview DOM: keeps the same Html5Qrcode instance restartable,
    // used to pause scanning while a result is displayed.
    pauseScanner: function () {
      var self = this;
      if (!this.cameraActive) {
        return Promise.resolve();
      }
      return this.runExclusive(function () {
        return self.stopCamera(false).then(function () {
          self.state = 'stopped';
        });
      });
    },
    // Full stop + clear(): used for manual stop, camera change (via onCameraChange's own
    // stopCamera() call), and unmount.
    fullStop: function () {
      var self = this;
      return this.runExclusive(function () {
        return self.stopCamera(true).then(function () {
          self.state = 'stopped';
        });
      });
    },
    onDecoded: function (decodedText) {
      if (this.isProcessing) {
        return;
      }
      var token = (decodedText || '').trim();
      if (!token) {
        return;
      }
      this.isProcessing = true;
      this.submitScan(token);
    },
    submitScan: function (token) {
      var self = this;
      return fetch(this.scanUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-TOKEN': csrfToken(),
        },
        body: JSON.stringify({ token: token }),
      }).then(function (response) {
        if (response.status === 201) {
          return response.json().then(function (data) {
            return self.pauseScanner().then(function () {
              self.showResult(data);
            });
          });
        }

        self.isProcessing = false;

        if (response.status === 422 || response.status === 404) {
          self.statusMessage = self.labels.invalidQrCode;
        } else {
          self.statusMessage = self.labels.scanRequestFailed;
        }
      }).catch(function () {
        self.isProcessing = false;
        self.statusMessage = self.labels.scanRequestFailed;
      });
    },
    showResult: function (data) {
      var self = this;
      this.result = {
        id: data.id,
        appointmentId: data.appointment_id,
        candidateNames: data.candidate_names,
        scheduledAt: data.scheduled_at,
        propertyName: data.property_name,
        wasPresent: !!data.was_present,
        isPromising: !!data.is_promising,
      };
      this.statusMessage = this.labels.candidateMarkedPresent;
      this.resultVisible = true;
      this.$nextTick(function () {
        if (self.$refs.resultHeading) {
          self.$refs.resultHeading.focus();
        }
      });
    },
    togglePromising: function () {
      if (!this.result || this.promisingLoading) {
        return;
      }

      var self = this;
      var previous = this.result.isPromising;
      var url = this.promisingUrlTemplate
        .replace('APPOINTMENT_ID', this.result.appointmentId)
        .replace('ASSIGNMENT_ID', this.result.id);

      this.result.isPromising = !previous;
      this.promisingLoading = true;

      return fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-TOKEN': csrfToken(),
        },
      }).then(function (response) {
        if (!response.ok) {
          throw new Error('request_failed');
        }
        return response.json();
      }).then(function (data) {
        self.result.isPromising = !!data.is_promising;
      }).catch(function () {
        self.result.isPromising = previous;
        self.statusMessage = self.labels.scanRequestFailed;
      }).finally(function () {
        self.promisingLoading = false;
      });
    },
    restoreFocusToTrigger: function () {
      var self = this;
      this.$nextTick(function () {
        if (self.$refs.startButton) {
          self.$refs.startButton.focus();
        }
      });
    },
    closeResult: function () {
      this.resultVisible = false;
      this.result = null;
      this.isProcessing = false;
      this.statusMessage = this.labels.scannerStopped;
      this.restoreFocusToTrigger();
    },
    scanNext: function () {
      this.resultVisible = false;
      this.result = null;
      this.isProcessing = false;
      this.restoreFocusToTrigger();
      if (!this.manuallyStopped) {
        this.handleStart();
      }
    },
  },
};
</script>
